/**
 * Cross-reference link spans in Word HTML (read path inject + insert path restore).
 *
 * A cross-reference link is an inline REF field (display text like "7.5.3" or "Table 8–2")
 * that points at a bookmark anchor elsewhere in the document.
 *
 * Read path (selection → LLM): Word's getHtml() shows link display text as plain text. It does
 * not include which bookmark each link targets. We scrape OOXML separately
 * (`extractCrossReferenceLinksFromOoxml`) and wrap each known display string in an
 * mso-field-code span so the model can preserve links when rewriting. Spans use single
 * backslashes in the REF instruction (`\h`, `\*MERGEFORMAT`) inside `mso-field-code`.
 *
 * Insert path (LLM → Word): sanitizeForInsert parses HTML in the DOM; when it serializes,
 * quotes inside `mso-field-code:"…"` become `&quot;`, which Word treats as styled plain text
 * instead of a field. `restoreWordMsoFieldCodeSpans` restores single-quoted spans and doubles
 * REF backslashes (Word insert API strips one level) so insertHtml() produces live cross-reference links.
 */

import type { CrossReferenceLink } from './cross-reference-links-from-ooxml'

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function alreadyWrapped(html: string, bookmarkId: string, text: string): boolean {
  if (!html.includes('mso-field-code') || !html.includes(bookmarkId)) return false
  return new RegExp(
    `mso-field-code[^>]*["']\\s*REF\\s+${escapeRegExp(bookmarkId)}[^"']*["'][^>]*>${escapeRegExp(text)}<`,
    'i'
  ).test(html)
}

function isInsideMsoFieldCodeSpan(html: string, index: number): boolean {
  const before = html.slice(0, index)
  const fieldMarker = 'mso-field-code:"'
  const markerIndex = before.lastIndexOf(fieldMarker)
  if (markerIndex === -1) return false

  const spanStart = before.lastIndexOf('<span', markerIndex)
  if (spanStart === -1) return false

  const closingTagIndex = html.indexOf('</span>', spanStart)
  if (closingTagIndex === -1) return true

  return index < closingTagIndex
}

/** Replace the first plain-text occurrence that is not already inside a cross-reference span. */
function wrapFirstUnwrappedPlainTextOccurrence(
  html: string,
  text: string,
  replacement: string
): string {
  let searchStart = 0

  while (searchStart <= html.length) {
    const index = html.indexOf(text, searchStart)
    if (index === -1) return html

    if (!isInsideMsoFieldCodeSpan(html, index)) {
      return `${html.slice(0, index)}${replacement}${html.slice(index + text.length)}`
    }

    searchStart = index + text.length
  }

  return html
}

/**
 * Wrap scraped cross-reference link display text with mso-field-code spans so the LLM
 * sees bookmark targets, not bare text.
 */
export function injectCrossReferenceLinkFields(
  html: string,
  crossReferenceLinks: CrossReferenceLink[]
): string {
  if (crossReferenceLinks.length === 0) {
    return html
  }

  let result = html

  for (const link of crossReferenceLinks) {
    // Default to a link when switches are unspecified (older callers/tests only ever meant links).
    const { displayText, bookmarkId, switches = '\\h \\*MERGEFORMAT' } = link
    if (!displayText || alreadyWrapped(result, bookmarkId, displayText)) continue

    const span = `<span style='mso-field-code:" REF ${bookmarkId}${switches ? ` ${switches}` : ''} "'>${displayText}</span>`
    result = wrapFirstUnwrappedPlainTextOccurrence(result, displayText, span)
  }

  return result
}

/**
 * When the DOM serializes, quotes inside `mso-field-code:"…"` become `&quot;`, which Word treats as styled plain text
 * Restore the single-quoted spans and double the REF backslashes (Word insert API strips one level)
 */
export function restoreWordMsoFieldCodeSpans(html: string): string {
  if (!html.includes('mso-field-code')) return html

  return html.replace(
    /<span\s+style="mso-field-code:&quot;([\s\S]*?)&quot;">([\s\S]*?)<\/span>/gi,
    (_match, fieldInstruction: string, displayText: string) => {
      // Double `\h` / `\*` for Word insertHtml (idempotent when already doubled).
      const forWordInsert = fieldInstruction.replace(/(^|\s)\\([h*])/g, '$1\\\\$2')
      return `<span style='mso-field-code:"${forWordInsert}"'>${displayText}</span>`
    }
  )
}
